import { useState } from 'react';
import {
  Box,
  Grid,
  Heading,
  HStack,
  VStack,
  Progress,
  Text,
  Icon,
  Button,
  ButtonGroup,
  Input,
  IconButton,
} from '@chakra-ui/react';
import { CheckIcon, CloseIcon, EditIcon } from '@chakra-ui/icons';
import { FiTrendingUp, FiCalendar, FiDollarSign, FiStar } from 'react-icons/fi';
import { VTODocument } from '../../types';
import {
  formatCurrency,
  parseTargetInput,
  fmtTargetDisplay,
  calculateHorizonProgress,
} from '../vto.util';

interface ThreeYearProgressProps {
  vto: VTODocument;
  onUpdate: (patch: Partial<VTODocument>) => Promise<boolean>;
}

type ActualField = 'revenue' | 'profit' | 'loans' | 'reviews';

/**
 * Progress Dashboard
 * Compares actuals vs. targets for the selected horizon (1-Year or 3-Year):
 * revenue, profit, and 5-star Google review conversion, plus a time-progress bar.
 * Actuals are inline-editable and persist via `onUpdate` (Firestore merge).
 */
export function ThreeYearProgress({ vto, onUpdate }: ThreeYearProgressProps) {
  const has3yr = (vto.threeYearRevenueTarget ?? 0) > 0 || (vto.threeYearProfitTarget ?? 0) > 0;
  const has1yr = (vto.oneYearRevenueTarget ?? 0) > 0 || (vto.oneYearProfitTarget ?? 0) > 0;

  const [horizon, setHorizon] = useState<'1' | '3'>(has3yr ? '3' : '1');
  const [editingField, setEditingField] = useState<ActualField | null>(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  // Only render the dashboard when at least one revenue/profit target is set.
  if (!has3yr && !has1yr) {
    return null;
  }

  const isThree = horizon === '3';
  const revenueTarget = isThree ? (vto.threeYearRevenueTarget ?? 0) : (vto.oneYearRevenueTarget ?? 0);
  const profitTarget = isThree ? (vto.threeYearProfitTarget ?? 0) : (vto.oneYearProfitTarget ?? 0);
  const forecastedLoans = isThree ? (vto.threeYearForecastedLoans ?? 0) : (vto.oneYearForecastedLoans ?? 0);
  const reviewTargetPct = isThree ? (vto.threeYearReviewTargetPct ?? 0) : (vto.oneYearReviewTargetPct ?? 0);
  const targetDate = isThree ? vto.threeYearDate : vto.oneYearDate;
  const totalMonths = isThree ? 36 : 12;

  const currentRevenue = vto.currentRevenue ?? 0;
  const currentProfit = vto.currentProfit ?? 0;
  const currentFundedLoans = vto.currentFundedLoans ?? 0;
  const currentReviews = vto.currentReviews ?? 0;

  const revenueProgress = revenueTarget > 0 ? (currentRevenue / revenueTarget) * 100 : 0;
  const profitProgress = profitTarget > 0 ? (currentProfit / profitTarget) * 100 : 0;

  const { elapsed, total } = calculateHorizonProgress(targetDate, totalMonths, vto.createdAt);
  const timeProgress = total > 0 ? (elapsed / total) * 100 : 0;

  const isRevenueOnTrack = revenueProgress >= timeProgress;
  const isProfitOnTrack = profitProgress >= timeProgress;

  const targetReviews = Math.round(forecastedLoans * (reviewTargetPct / 100));
  const conversionRate = currentFundedLoans > 0 ? (currentReviews / currentFundedLoans) * 100 : 0;
  const showReviews = forecastedLoans > 0 && reviewTargetPct > 0;

  const startEdit = (field: ActualField, initialDraft: string) => {
    setEditingField(field);
    setDraft(initialDraft);
  };
  const cancelEdit = () => {
    setEditingField(null);
    setDraft('');
  };
  const saveEdit = async (field: ActualField) => {
    setSaving(true);
    const patch: Partial<VTODocument> = {};
    if (field === 'revenue') patch.currentRevenue = parseTargetInput(draft);
    else if (field === 'profit') patch.currentProfit = parseTargetInput(draft);
    else if (field === 'loans') patch.currentFundedLoans = Math.max(0, parseInt(draft.replace(/[^0-9]/g, ''), 10) || 0);
    else if (field === 'reviews') patch.currentReviews = Math.max(0, parseInt(draft.replace(/[^0-9]/g, ''), 10) || 0);
    const ok = await onUpdate(patch);
    setSaving(false);
    if (ok) cancelEdit();
  };

  // Returns inline JSX (NOT a nested component) so the edit Input keeps focus across keystrokes.
  const renderEditable = (field: ActualField, formatted: string, initialDraft: string) => {
    if (editingField === field) {
      return (
        <HStack spacing={1}>
          <Input
            size="xs"
            width="110px"
            value={draft}
            autoFocus
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveEdit(field);
              if (e.key === 'Escape') cancelEdit();
            }}
          />
          <IconButton aria-label="Save" size="xs" colorScheme="green" icon={<CheckIcon />} isLoading={saving} onClick={() => saveEdit(field)} />
          <IconButton aria-label="Cancel" size="xs" variant="ghost" icon={<CloseIcon />} onClick={cancelEdit} />
        </HStack>
      );
    }
    return (
      <HStack spacing={1} role="group">
        <Text fontWeight="semibold" color="gray.900">{formatted}</Text>
        <IconButton
          aria-label="Edit value"
          size="xs"
          variant="ghost"
          icon={<EditIcon />}
          opacity={0}
          _groupHover={{ opacity: 1 }}
          onClick={() => startEdit(field, initialDraft)}
        />
      </HStack>
    );
  };

  return (
    <Box bgGradient="linear(to-br, blue.50, green.50)" borderWidth="1px" borderColor="blue.200" rounded="lg" p={6} shadow="sm">
      <HStack justify="space-between" mb={4} flexWrap="wrap" gap={2}>
        <HStack spacing={2}>
          <Icon as={FiTrendingUp} w={5} h={5} color="blue.600" />
          <Heading size="md" color="gray.900">{isThree ? '3-Year' : '1-Year'} Progress Dashboard</Heading>
          {targetDate && (
            <Text fontSize="sm" color="gray.600" ml={2}>(Target: {targetDate})</Text>
          )}
        </HStack>
        <ButtonGroup size="sm" isAttached variant="outline">
          <Button onClick={() => setHorizon('1')} colorScheme={!isThree ? 'blue' : 'gray'} variant={!isThree ? 'solid' : 'outline'}>
            1-Year
          </Button>
          <Button onClick={() => setHorizon('3')} colorScheme={isThree ? 'blue' : 'gray'} variant={isThree ? 'solid' : 'outline'}>
            3-Year
          </Button>
        </ButtonGroup>
      </HStack>

      <Grid templateColumns={{ base: '1fr', md: showReviews ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)' }} gap={6}>
        {revenueTarget > 0 && (
          <Box bg="white" rounded="lg" p={4} shadow="sm">
            <HStack spacing={2} mb={2}>
              <Icon as={FiDollarSign} w={4} h={4} color="green.600" />
              <Text fontSize="md" fontWeight="semibold" color="gray.700">Revenue Progress</Text>
            </HStack>
            <VStack spacing={2} align="stretch">
              <HStack justify="space-between" fontSize="sm">
                <Text color="gray.600">Current:</Text>
                {renderEditable('revenue', formatCurrency(currentRevenue), fmtTargetDisplay(currentRevenue))}
              </HStack>
              <HStack justify="space-between" fontSize="sm">
                <Text color="gray.600">Target:</Text>
                <Text fontWeight="semibold" color="gray.900">{formatCurrency(revenueTarget)}</Text>
              </HStack>
              <Progress value={Math.min(revenueProgress, 100)} colorScheme={isRevenueOnTrack ? 'green' : 'red'} size="sm" rounded="full" />
              <HStack justify="space-between" align="center" fontSize="sm">
                <Text fontWeight="medium" color={isRevenueOnTrack ? 'green.600' : 'red.600'}>{revenueProgress.toFixed(1)}% Complete</Text>
                <Text fontSize="xs" color={isRevenueOnTrack ? 'green.600' : 'red.600'}>{isRevenueOnTrack ? 'On pace' : 'Behind pace'}</Text>
              </HStack>
            </VStack>
          </Box>
        )}

        {profitTarget > 0 && (
          <Box bg="white" rounded="lg" p={4} shadow="sm">
            <HStack spacing={2} mb={2}>
              <Icon as={FiDollarSign} w={4} h={4} color="blue.600" />
              <Text fontSize="md" fontWeight="semibold" color="gray.700">Profit Progress</Text>
            </HStack>
            <VStack spacing={2} align="stretch">
              <HStack justify="space-between" fontSize="sm">
                <Text color="gray.600">Current:</Text>
                {renderEditable('profit', formatCurrency(currentProfit), fmtTargetDisplay(currentProfit))}
              </HStack>
              <HStack justify="space-between" fontSize="sm">
                <Text color="gray.600">Target:</Text>
                <Text fontWeight="semibold" color="gray.900">{formatCurrency(profitTarget)}</Text>
              </HStack>
              <Progress value={Math.min(profitProgress, 100)} colorScheme={isProfitOnTrack ? 'blue' : 'red'} size="sm" rounded="full" />
              <HStack justify="space-between" align="center" fontSize="sm">
                <Text fontWeight="medium" color={isProfitOnTrack ? 'blue.600' : 'red.600'}>{profitProgress.toFixed(1)}% Complete</Text>
                <Text fontSize="xs" color={isProfitOnTrack ? 'blue.600' : 'red.600'}>{isProfitOnTrack ? 'On pace' : 'Behind pace'}</Text>
              </HStack>
            </VStack>
          </Box>
        )}

        {showReviews && (
          <Box bg="white" rounded="lg" p={4} shadow="sm">
            <HStack spacing={2} mb={2}>
              <Icon as={FiStar} w={4} h={4} color="orange.400" />
              <Text fontSize="md" fontWeight="semibold" color="gray.700">5-Star Google Reviews</Text>
            </HStack>
            <VStack spacing={2} align="stretch">
              <HStack justify="space-between" fontSize="sm">
                <Text color="gray.600">Funded Loans:</Text>
                {renderEditable('loans', currentFundedLoans.toLocaleString(), String(currentFundedLoans || ''))}
              </HStack>
              <HStack justify="space-between" fontSize="sm">
                <Text color="gray.600">Reviews:</Text>
                {renderEditable('reviews', currentReviews.toLocaleString(), String(currentReviews || ''))}
              </HStack>
              <HStack justify="space-between" fontSize="sm">
                <Text color="gray.600">Target Reviews:</Text>
                <Text fontWeight="semibold" color="gray.900">{targetReviews.toLocaleString()}</Text>
              </HStack>
              <Progress value={targetReviews > 0 ? Math.min((currentReviews / targetReviews) * 100, 100) : 0} colorScheme="orange" size="sm" rounded="full" />
              <Text fontSize="xs" color="gray.600">Conversion: {conversionRate.toFixed(1)}% ({reviewTargetPct}% target)</Text>
            </VStack>
          </Box>
        )}
      </Grid>

      <Box mt={4} bg="white" rounded="lg" p={4} shadow="sm">
        <HStack spacing={2} mb={2}>
          <Icon as={FiCalendar} w={4} h={4} color="gray.600" />
          <Text fontSize="md" fontWeight="semibold" color="gray.700">Time Progress</Text>
        </HStack>
        <VStack spacing={2} align="stretch">
          <HStack justify="space-between" fontSize="sm">
            <Text color="gray.600">Elapsed:</Text>
            <Text fontWeight="semibold" color="gray.900">{elapsed} of {total} months</Text>
          </HStack>
          <Progress value={Math.min(timeProgress, 100)} colorScheme="gray" size="sm" rounded="full" />
          <Text fontSize="sm" color="gray.600">{timeProgress.toFixed(1)}% of timeline complete</Text>
        </VStack>
      </Box>
    </Box>
  );
}
